import * as HoverCard from "@radix-ui/react-hover-card";

import type { Citation } from "../../types/agent-chat";
import { chipLabel } from "./citationFormat";
import { SourcePreview } from "./SourcePreview";

const KIND_STYLES: Record<string, string> = {
  document: "bg-brand-accent/10 text-brand-accent-dk hover:bg-brand-accent hover:text-white",
  web: "bg-brand-teal/10 text-brand-primary hover:bg-brand-teal hover:text-white",
  cnp: "bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-white",
};

export interface CitationMarkerProps {
  /** Comma-separated ids, as the remark plugin emits them. */
  ids: string;
  citations: Record<string, Citation>;
  onOpenDocument?: (citation: Citation) => void;
}

/**
 * Inline citation chips.
 *
 * An id with no matching citation is rendered as a muted chip rather than
 * dropped: the server already guarantees every surviving marker was in the
 * candidate set for that turn, so a miss here means the client lost the
 * `sources` event, and hiding it would hide the bug.
 */
export function CitationMarker({ ids, citations, onOpenDocument }: CitationMarkerProps) {
  const list = ids
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <span className="inline-flex items-baseline gap-0.5 align-baseline">
      {list.map((id) => {
        const citation = citations[id];
        if (!citation) {
          return (
            <span
              key={id}
              title="Source indisponible dans cette session"
              className="ml-0.5 rounded px-1 text-[11px] font-semibold text-brand-muted"
            >
              {chipLabel(id)}
            </span>
          );
        }
        return (
          <HoverCard.Root key={id} openDelay={120} closeDelay={80}>
            <HoverCard.Trigger asChild>
              <button
                type="button"
                onClick={() => onOpenDocument?.(citation)}
                aria-label={`Source ${id}: ${citation.title}`}
                className={`ml-0.5 rounded px-1 text-[11px] font-semibold transition-colors ${
                  KIND_STYLES[citation.kind] ?? KIND_STYLES.document
                }`}
              >
                {chipLabel(id)}
              </button>
            </HoverCard.Trigger>
            <HoverCard.Portal>
              <HoverCard.Content
                side="top"
                align="start"
                sideOffset={6}
                className="z-50 w-80 rounded-xl border border-brand-border bg-brand-bg p-3 shadow-deep"
              >
                <SourcePreview citation={citation} onOpenDocument={onOpenDocument} />
                <HoverCard.Arrow className="fill-brand-bg" />
              </HoverCard.Content>
            </HoverCard.Portal>
          </HoverCard.Root>
        );
      })}
    </span>
  );
}
